import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Hand, Loader2, PanelLeftOpen, Search, Smartphone } from "lucide-react";
import { AppShell, PageHeader } from "@/components/AppShell";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CloudPhoneStream } from "@/components/vmos/CloudPhoneStream";
import { fetchDevices, type VMOSDevice } from "@/services/vmos-api";
import { cn } from "@/lib/utils";

export default function ManualDevicesPage() {
	const [selected, setSelected] = useState<VMOSDevice | null>(null);
	const [query, setQuery] = useState("");
	const queryClient = useQueryClient();

	const { data, isLoading, isError, error } = useQuery({
		queryKey: ["vmos-devices"],
		queryFn: fetchDevices,
		staleTime: 30_000,
	});

	const devices = useMemo(() => {
		const list = data?.devices ?? [];
		const q = query.trim().toLowerCase();
		const filtered = q
			? list.filter(
					(d) =>
						d.device_name?.toLowerCase().includes(q) ||
						d.pad_id.toLowerCase().includes(q) ||
						d.username?.toLowerCase().includes(q)
				)
			: list;
		return filtered.slice().sort((a, b) => a.device_name.localeCompare(b.device_name));
	}, [data, query]);

	return (
		<AppShell>
			<PageHeader>
				<SidebarTrigger className="data-[state=open]:hidden text-primary">
					<PanelLeftOpen className="h-4 w-4" />
				</SidebarTrigger>
				{selected ? (
					<Button
						variant="ghost"
						size="sm"
						className="-ml-2"
						onClick={() => setSelected(null)}
					>
						<ArrowLeft className="h-4 w-4 mr-1" /> Devices
					</Button>
				) : (
					<div className="flex flex-col">
						<h1 className="text-lg font-semibold leading-tight">Manual</h1>
						<p className="text-xs text-muted-foreground">
							Take direct control of a cloud phone, like a live simulator.
						</p>
					</div>
				)}
			</PageHeader>

			{selected ? (
				<div className="flex-1 min-h-0">
					<CloudPhoneStream
						padId={selected.pad_id}
						deviceName={selected.device_name}
						onClose={() => {
							setSelected(null);
							// Release changed the device's busy state server-side.
							queryClient.invalidateQueries({ queryKey: ["vmos-devices"] });
						}}
					/>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
					<div className="max-w-4xl mx-auto space-y-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search devices…"
								className="pl-9"
							/>
						</div>

						{isLoading ? (
							<div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
								Loading devices…
							</div>
						) : isError ? (
							<div className="flex items-center justify-center py-16 text-destructive text-sm">
								{(error as Error)?.message || "Failed to load devices"}
							</div>
						) : devices.length === 0 ? (
							<div className="flex flex-col items-center gap-2 py-20 text-center">
								<Smartphone className="h-8 w-8 text-muted-foreground/40" />
								<div className="text-sm font-medium">No devices</div>
								<p className="text-xs text-muted-foreground">
									Devices assigned to you will show up here.
								</p>
							</div>
						) : (
							<div className="border rounded-lg divide-y">
								{devices.map((device) => (
									<button
										key={device.pad_id}
										onClick={() => setSelected(device)}
										className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
									>
										<Hand className="h-4 w-4 text-muted-foreground shrink-0" />
										<div className="min-w-0 flex-1">
											<div className="text-sm font-medium truncate flex items-center gap-2">
												{device.device_name}
												{device.username && (
													<span className="text-xs font-normal text-muted-foreground">
														@{device.username}
													</span>
												)}
											</div>
											<div className="text-[11px] font-mono text-muted-foreground truncate">
												{device.pad_id}
											</div>
										</div>
										<Badge variant="outline" className="text-[10px] capitalize shrink-0">
											{device.platform}
										</Badge>
										<div
											className={cn(
												"w-2 h-2 rounded-full shrink-0",
												device.busy
													? "bg-amber-500"
													: device.status === "active"
														? "bg-green-500"
														: "bg-gray-400"
											)}
											title={device.busy ? `Busy (${device.busy_kind ?? "in use"})` : "Available"}
										/>
									</button>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</AppShell>
	);
}
